/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { usePatch, globalErrorMsg, AdFormat, getAccountCurrencyCode, bidStrategyOptionKeys, isAdFormatValid } from '@bingads-webui-campaign/hotel-common-upsert';
import { checkShouldShowError, checkShouldShowErrors, CampaignName, isCampaignNameValid, LocalizationKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import { Collapse, TextTitle } from '@bingads-webui-campaign/wizard-jumbo-card';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import classNames from 'classnames';
import { Link, Stack } from '@bingads-webui-react/fabric-control';
import { CampaignBudget } from './budget';
import { CampaignBidStrategy } from './bid-strategy';
import { isBudgetValid } from './budget/utility';
import { isBidStrategyValid, fields } from './bid-strategy/utility';

const getStyles = () =>
  ({
    basicCampaignSection: {
      '& label': {
        padding: 'unset',
      },
      '& .section ~ .section': {
        marginTop: '24px',
      },
      '& .description': {
        marginBottom: '8px',
      },
      '& label, .helpbutton': {
        display: 'inline-block',
      },
      '& .individual-budget-editor': {
        padding: '4px 0 10px 26px',
      },
    },
  });

export const CampaignBasic = (props) => {
  const {
    campaign,
    patchCampaign,
    scenario,
    getAdScheduleView,
    permissions,
    activity,
  } = props;

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const classes = useDefaultStyles(getStyles);
  const setName = usePatch(patchCampaign, 'name');
  const setBudget = usePatch(patchCampaign, 'budget');
  const setAdFormat = usePatch(patchCampaign, 'adFormat');
  const setBidStrategy = usePatch(patchCampaign, 'bidStrategy');
  const isCreation = scenario === 'creation';

  const allPanelItems = ['campaign', 'budget', 'bidStrategy'];
  const [activeKeys, setActiveKeys] = useState(allPanelItems);
  const onCollapseAll = () => {
    if (activeKeys.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(allPanelItems);
    }
  };

  const getBudgetTitleDetails = () => {
    if (campaign.budget && campaign.budget.amount) {
      if (campaign.budget.option === 'Individual') {
        const { amount } = campaign.budget;
        const accountCurrencyCode = getAccountCurrencyCode(serverContext);
        return i18n.getString(_TL_('Spend {{amount}} {{accountCurrencyCode}}/Day (avg.)'), { amount, accountCurrencyCode });
      }
    }
    return '';
  };

  const getBidStrategyTitleDetails = () => {
    const { bidStrategy } = campaign;
    const percentageOrBid = bidStrategy && bidStrategy[campaign.bidStrategy.option];
    const accountCurrencyCode = getAccountCurrencyCode(serverContext);
    let detail = '';
    switch (campaign.bidStrategy.option) {
      case bidStrategyOptionKeys.ManualCpc:
        detail = `${i18n.getString(_TL_('Bid management'))}: ${i18n.getString(LocalizationKeys[campaign.bidStrategy.option])}`;
        break;
      case bidStrategyOptionKeys.PercentCpc:
        detail = `${i18n.getString(_TL_('Bid management'))}: ${i18n.getString(LocalizationKeys[campaign.bidStrategy.option])}, ${i18n.getString(_TL_('Maximum effective bid limit'))}: ${percentageOrBid ? `${percentageOrBid} ${accountCurrencyCode}` : ''}`;
        break;
      case bidStrategyOptionKeys.Commission:
        detail = `${i18n.getString(_TL_('Bid management'))}: ${i18n.getString(LocalizationKeys[campaign.bidStrategy.option])}, ${i18n.getString(_TL_('Room price (per stay)'))}: ${percentageOrBid ? `${percentageOrBid}%` : ''}`;
        break;
      default:
        detail = '';
        break;
    }
    if (isCreation) {
      switch (campaign.bidStrategy.option) {
        case bidStrategyOptionKeys.ManualCpc:
          detail += `, ${i18n.getString(_TL_('Default ad group bid'))}: ${bidStrategy[fields.AdGroupBid] ? `${bidStrategy[fields.AdGroupBid]} ${accountCurrencyCode}` : ''}`;
          break;
        case bidStrategyOptionKeys.PercentCpc:
          detail += `, ${i18n.getString(_TL_('Default ad group percent bid'))}: ${bidStrategy[fields.AdGroupPercentBid] ? `${bidStrategy[fields.AdGroupPercentBid]}%` : ''}`;
          break;
        case bidStrategyOptionKeys.Commission:
          break;
        default:
          detail = '';
          break;
      }
    }
    return detail;
  };

  const getCampaignTitleDetail = () => {
    const details = [];
    details.push(`${i18n.getString(_TL_('Campaign name'))}: ${campaign.name.value ? campaign.name.value : ''}`);
    if (campaign.adFormat) {
      const entries = ['HPA', 'PPA'];
      const adFormatValue = _.compact(entries.map((entry) => { if (campaign.adFormat[entry]) { return entry; } return false; })).join(', ');
      if (adFormatValue) {
        const IsRenameHotelToLodgingEnabled = permissions && permissions.dynamic && permissions.dynamic.IsRenameHotelToLodgingEnabled;
        const detailString = IsRenameHotelToLodgingEnabled ? `${i18n.getString(_TL_('Property ad format'))}: ${adFormatValue}` : `${i18n.getString(_TL_('Hotel ad format'))}: ${adFormatValue}`;
        details.push(detailString);
      }
    }
    return details.join(', ');
  };
  const showBudgetMinRecommend = permissions.dynamic.BCCBudgetImprovementEnabled && !isCreation && campaign.curBudgetAmount && campaign.curBudgetAmount >= 0.1 && campaign.budget.amount && ((campaign.budget.amount * 1.0) < (campaign.curBudgetAmount * 0.5).toFixed(2));

  return (
    <div className={classNames('basic-campaign-section', classes.basicCampaignSection)}>
      <Stack horizontalAlign="end" tokens={{ padding: '0 0 12px 0' }}>
        <Link className="jumbo-card-control-all-btn" onClick={onCollapseAll}>{activeKeys.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
      </Stack>
      <Collapse activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
        <Collapse.Panel
          key="campaign"
          title={<TextTitle titleKey={_TL_('Campaign')} details={isCreation ? getCampaignTitleDetail() : campaign.name.value} />}
          error={((!isCampaignNameValid(campaign.name.value) && checkShouldShowError(campaign.name)) || !isAdFormatValid(campaign.adFormat) || checkShouldShowErrors(campaign.name)) ? globalErrorMsg : ''}
        >
          <CampaignName name={campaign.name} setName={setName} />
          {isCreation && <AdFormat adFormat={campaign.adFormat} setAdFormat={setAdFormat} scenario={scenario} permissions={permissions} />}
        </Collapse.Panel>
        <Collapse.Panel
          key="budget"
          title={<TextTitle titleKey={_TL_('Budget')} details={getBudgetTitleDetails()} />}
          error={!isBudgetValid({ value: campaign.budget.amount, serverContext }) && checkShouldShowError(campaign.budget) ? globalErrorMsg : ''}
        >
          <CampaignBudget showBudgetMinRecommend={showBudgetMinRecommend} curBudgetAmount={campaign.curBudgetAmount} budget={campaign.budget} setBudget={setBudget} activity={activity} />
        </Collapse.Panel>
        <Collapse.Panel
          key="bidStrategy"
          title={<TextTitle titleKey={_TL_('Bid strategy')} details={getBidStrategyTitleDetails()} />}
          error={!isBidStrategyValid({ bidStrategy: campaign.bidStrategy, isCreation, serverContext }) && checkShouldShowError(campaign.bidStrategy) ? globalErrorMsg : ''}
        >
          <CampaignBidStrategy permissions={permissions} bidStrategy={campaign.bidStrategy} setBidStrategy={setBidStrategy} scenario={scenario} getAdScheduleView={getAdScheduleView} />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

CampaignBasic.propTypes = {
  campaign: PropTypes.shape({}).isRequired,
  patchCampaign: PropTypes.func.isRequired,
  scenario: PropTypes.string.isRequired,
  errMsg: PropTypes.shape({}),
  getAdScheduleView: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      IsRenameHotelToLodgingEnabled: PropTypes.bool,
    }),
  }),
  activity: PropTypes.shape({}).isRequired,
};

CampaignBasic.defaultProps = {
  errMsg: {},
  permissions: {
    dynamic: {
      IsRenameHotelToLodgingEnabled: false,
    },
  },
};
